import React, { JSX, useEffect, useRef } from "react";
import styled from "styled-components";
import { mobile } from "@ui/styles/utils";
import { row } from "./layout";

interface TabSelectorProps<T> extends React.HTMLAttributes<HTMLDivElement> {
	items: readonly T[];
	selectedIndex: number;
	itemRenderer: (item: T) => JSX.Element | string | null | undefined;
	onItemSelected: (index: number, tab?: T) => void;
	keyExtractor?: (item: T) => string;
	scrollToEnd?: boolean;
}
export function TabSelector<T>({
	items,
	selectedIndex,
	itemRenderer,
	onItemSelected,
	keyExtractor,
	scrollToEnd,
	...htmlProps
}: TabSelectorProps<T>): JSX.Element {
	const scrollingDiv = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!scrollingDiv.current || !scrollToEnd) {
			return;
		}
		scrollingDiv.current.scrollTo({
			left: scrollingDiv.current?.scrollWidth,
			behavior: "smooth",
		});
	}, [scrollingDiv, scrollToEnd]);

	return (
		<Container {...htmlProps} ref={scrollingDiv}>
			{items &&
				items.map((item, i) => (
					<Tab key={keyExtractor?.(item) ?? i} selected={selectedIndex === i} onClick={() => onItemSelected(i, item)}>
						{itemRenderer(item)}
					</Tab>
				))}
		</Container>
	);
}

const Container = styled.div`
	${row(65)};

	@media ${mobile} {
		${row(32)};
		overflow-x: scroll;
	}
`;

const Tab = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>`
	${({ theme }) => theme.textStyles.primaryBold};
	${({ selected, theme }) => selected && `color: ${theme.colors.secondary}`};
	position: relative;
	cursor: pointer;
	transition: color 0.2s linear;
	white-space: nowrap;
	padding-bottom: 4px;
`;
